import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-resource-router';

import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import { IntervalRenderer } from 'src/components/pipelines/common';
import {
  messages,
  TestMetricValue,
  TestExecutedDate,
  TestChildWrapper,
  TestTableHeader,
  TestTextWrapper,
  TestOutcome,
  TestManagementTable,
  CommitMessageWrapper,
  BuildInformationWrappper,
  type TestCase,
  type TestExecutions,
} from 'src/components/pipelines/components/test-management';
import {
  formatTestDurations,
  PAGE_LEN,
} from 'src/components/pipelines/components/test-management/utils';
import { formatDistanceToNow } from 'src/components/pipelines/utils';
import durationToSeconds from 'src/components/pipelines/utils/durationToSeconds';
import {
  getStepPageURL,
  getCommitPageURL,
} from 'src/components/pipelines/utils/links';
import { getPages } from 'src/components/pipelines/utils/pagination';
import { useAnalytics } from 'src/components/settings/analytics';

const tooltipWrapperStyles = xcss({
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
});

type Props = {
  testCase?: TestCase;
  testExecutions?: TestExecutions;
  repositoryFullSlug: string;
  isLoading?: boolean;
  updateMethod?: 'push' | 'replace';
};

export const TestExecutionTable = ({
  testCase,
  testExecutions,
  repositoryFullSlug,
  isLoading,
  updateMethod,
}: Props) => {
  const { formatMessage } = useIntl();
  const { publishButtonClickEvent } = useAnalytics({
    source: 'testExecutionTable',
  });

  const tableHead = useMemo(
    () => ({
      cells: [
        {
          key: 'build',
          content: (
            <TestTableHeader>{formatMessage(messages.build)}</TestTableHeader>
          ),
          width: 65,
        },
        {
          key: 'commit',
          content: (
            <TestTableHeader>{formatMessage(messages.commit)}</TestTableHeader>
          ),
          width: 10,
        },
        {
          key: 'createdOn',
          content: (
            <TestTableHeader>
              {formatMessage(messages.lastExecuted)}
            </TestTableHeader>
          ),
          width: 20,
          isSortable: true,
          sortKey: 'createdOn',
        },
        {
          key: 'duration',
          content: (
            <TestTableHeader>
              {formatMessage(messages.executionDuration)}
            </TestTableHeader>
          ),
          width: 15,
          isSortable: true,
        },
        {
          key: 'status',
          content: (
            <TestTableHeader>{formatMessage(messages.outcome)}</TestTableHeader>
          ),
          width: 10,
          isSortable: true,
        },
      ],
    }),
    [formatMessage]
  );

  const tableRows = useMemo(() => {
    return testExecutions?.values?.map(
      ({
        uuid,
        status,
        duration,
        target,
        createdOn,
        pipelineUuid,
        stepUuid,
        runUuid,
        buildNumber,
      }) => {
        const key = `row-${uuid}`;

        const executionTime = durationToSeconds(duration);
        const hasExecutionTimeWarning = testCase?.metrics.p90DurationInMillis
          ? executionTime > testCase?.metrics.p90DurationInMillis / 1000
          : false;
        const executionTimeContent = (
          <Tooltip
            canAppear={() => hasExecutionTimeWarning}
            content={formatMessage(messages.executionTimeWarningTooltip)}
            position="right"
          >
            <TestMetricValue hasWarning={hasExecutionTimeWarning}>
              {formatTestDurations(executionTime)}
            </TestMetricValue>
          </Tooltip>
        );

        return {
          key,
          cells: [
            {
              key: `${key}-build`,
              content: (
                <BuildInformationWrappper>
                  <Link
                    to={getStepPageURL(
                      { uuid: pipelineUuid },
                      stepUuid,
                      repositoryFullSlug,
                      runUuid
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      publishButtonClickEvent('buildNumber', {
                        status,
                      })
                    }
                  >
                    <TestTextWrapper>#{buildNumber}</TestTextWrapper>
                  </Link>
                  <Box xcss={tooltipWrapperStyles}>
                    <Tooltip content={target.commit.message}>
                      <CommitMessageWrapper>
                        {target.commit.message}
                      </CommitMessageWrapper>
                    </Tooltip>
                  </Box>
                </BuildInformationWrappper>
              ),
            },
            {
              key: `${key}-commit`,
              content: (
                <TestChildWrapper>
                  <Link
                    to={getCommitPageURL(repositoryFullSlug, target.revision)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <TestTextWrapper>
                      {target.revision?.slice(0, 7)}
                    </TestTextWrapper>
                  </Link>
                </TestChildWrapper>
              ),
            },
            {
              key: `${key}-executedDate`,
              content: (
                <TestExecutedDate>
                  <IntervalRenderer interval={15000}>
                    {() => formatDistanceToNow(createdOn)}
                  </IntervalRenderer>
                </TestExecutedDate>
              ),
            },
            {
              key: `${key}-executionTime`,
              content: executionTimeContent,
            },
            {
              key: `${key}-status`,
              content: <TestOutcome type={status} />,
            },
          ],
        };
      }
    );
  }, [
    formatMessage,
    testCase?.metrics.p90DurationInMillis,
    testExecutions,
    repositoryFullSlug,
    publishButtonClickEvent,
  ]);

  return (
    <TestManagementTable
      head={tableHead}
      rows={tableRows}
      pages={getPages(testExecutions?.size ?? 0, PAGE_LEN)}
      isLoading={isLoading}
      updateMethod={updateMethod}
    />
  );
};
